/**
 * Custom JavaScript for Fox Meadows theme
 * 
 * This file extends the base theme functionality with customizations.
 */

/**
 * Custom Progress Bar Component
 * 
 * A custom HTML element that automatically syncs with its parent free-shipping-bar
 * to display progress visualization. Observes threshold and total-price attributes
 * and updates the progress indicator accordingly.
 */
class CustomProgressBar extends HTMLElement {
  constructor() {
    super();
    this._observer = null;
    this._parentBar = null;
  }

  connectedCallback() {
    // Set ARIA role
    this.setAttribute('role', 'progressbar');
    this.setAttribute('aria-valuemin', '0');
    
    // Find parent free-shipping-bar element
    this._parentBar = this.closest('free-shipping-bar');
    
    if (!this._parentBar) {
      console.warn('custom-progress-bar must be a child of free-shipping-bar');
      return;
    }
    
    // Set up MutationObserver to watch parent for attribute changes
    this._observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          const attributeName = mutation.attributeName;
          
          if (attributeName === 'threshold') {
            this._updateMaxValue();
          } else if (attributeName === 'total-price') {
            this._updateCurrentValue();
          }
        }
      });
    });
    
    // Start observing parent
    this._observer.observe(this._parentBar, {
      attributes: true,
      attributeFilter: ['threshold', 'total-price']
    });
    
    // Trigger initial update
    this._updateMaxValue();
    this._updateCurrentValue();
  }

  disconnectedCallback() {
    // Clean up observer
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    this._parentBar = null;
  }

  /**
   * Update the maximum value (threshold) from parent
   * @private
   */
  _updateMaxValue() {
    if (!this._parentBar) return;
    
    const threshold = this._parentBar.getAttribute('threshold');
    if (threshold) {
      this.setAttribute('aria-valuemax', threshold);
      this._updateProgress();
    }
  }

  /**
   * Update the current value (total price) from parent
   * @private
   */
  _updateCurrentValue() {
    if (!this._parentBar) return;
    
    const totalPrice = this._parentBar.getAttribute('total-price');
    if (totalPrice) {
      this.setAttribute('aria-valuenow', totalPrice);
      this._updateProgress();
    }
  }

  /**
   * Calculate and update the progress CSS variable
   * @private
   */
  _updateProgress() {
    const valueNow = parseFloat(this.getAttribute('aria-valuenow')) || 0;
    const valueMax = parseFloat(this.getAttribute('aria-valuemax')) || 1;
    
    // Calculate progress (0 to 1), capped at 1 (100%)
    const progress = Math.min(valueNow / valueMax, 1);
    
    // Update CSS custom property for visual display
    this.style.setProperty('--progress', progress);
  }
}

// Register the custom element
if (!window.customElements.get('custom-progress-bar')) {
  window.customElements.define('custom-progress-bar', CustomProgressBar);
}

